// /app/frontend/src/components/frugal-v2/components/ZenLockScreen.jsx
// Full-screen lock overlay with PIN input (130 lines)

import React, { useState, useRef, useEffect } from 'react';
import { Lock, ShieldCheck, AlertCircle } from 'lucide-react';

const ZenLockScreen = ({ onUnlock, t }) => {
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only digits

    const newPin = [...pin];
    newPin[index] = value.slice(-1); // Only last digit
    setPin(newPin);
    setError(false);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when 4+ digits entered
    const enteredPin = newPin.join('');
    if (enteredPin.length >= 4 && newPin.slice(0, 4).every(d => d !== '')) {
      // Check if we should wait for more digits or submit
      const filledCount = newPin.filter(d => d !== '').length;
      if (filledCount === 4 || filledCount === 5 || filledCount === 6) {
        // Small delay to allow typing more digits
        setTimeout(() => {
          const currentPin = newPin.join('').replace(/\s/g, '');
          if (currentPin.length >= 4) {
            handleSubmit(currentPin);
          }
        }, 500);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      const enteredPin = pin.join('');
      if (enteredPin.length >= 4) {
        handleSubmit(enteredPin);
      }
    }
  };

  const handleSubmit = async (enteredPin) => {
    if (loading) return;
    setLoading(true);
    
    const success = await onUnlock(enteredPin);
    
    if (!success) {
      setError(true);
      setPin(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900 flex flex-col items-center justify-center p-6">
      {/* Logo/Icon */}
      <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mb-6">
        <Lock className="w-10 h-10 text-emerald-400" />
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-white mb-2">
        {t?.('frugal.zenLock.title')}
      </h1>
      <p className="text-slate-400 text-center mb-8">
        {t?.('frugal.zenLock.enterPin')}
      </p>

      {/* PIN Input */}
      <div className="flex gap-3 mb-6">
        {pin.map((digit, index) => (
          <input
            key={index}
            ref={el => inputRefs.current[index] = el}
            type="password"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 bg-slate-800 text-white focus:outline-none transition-colors ${
              error 
                ? 'border-red-500 shake-animation' 
                : digit 
                  ? 'border-emerald-500' 
                  : 'border-slate-600 focus:border-emerald-500'
            }`}
          />
        ))}
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 text-red-400 mb-4">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{t?.('frugal.zenLock.wrongPin')}</span>
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="flex items-center gap-2 text-emerald-400">
          <ShieldCheck className="w-5 h-5 animate-pulse" />
          <span className="text-sm">{t?.('frugal.zenLock.verifying')}</span>
        </div>
      )}

      {/* Footer hint */}
      <p className="absolute bottom-8 text-slate-500 text-xs text-center">
        {t?.('frugal.zenLock.hint')}
      </p>

      <style>{`
        .shake-animation {
          animation: shake 0.5s ease-in-out;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-5px); }
          40%, 80% { transform: translateX(5px); }
        }
      `}</style>
    </div>
  );
};

export default ZenLockScreen;
